/**
 * Page /grc/dossiers-petite-enfance — pièces des dossiers petite enfance.
 *
 * Jumelle des « Dossiers scolaires » côté GRC, adaptée à la bibliothèque de
 * pièces par famille (migration 110) :
 *   - Catalogue : l'admin/manager définit les natures de pièces réclamées en
 *     petite enfance (communes + spécifiques PE : vaccins, PAI…).
 *   - Familles : on choisit une famille pour consulter / valider ses pièces
 *     (déposées une fois, réutilisées partout — on ne redemande que ce qui manque).
 */

import { useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { FamillePiecesPanel } from '@/components/famille/FamillePiecesPanel';
import { AlertCircle, Baby, Search, Settings, Users } from 'lucide-react';
import type { PieceType } from '@/lib/api';
import type { ScolaireFamille } from '@roronoa-collectivites/shared-types';

export function GrcDossiersPetiteEnfancePage() {
  const { user } = useAuth();
  const isAdmin = !!user && ['super_admin', 'admin', 'manager'].includes(user.role);

  const [error, setError] = useState<string | null>(null);
  const [vue, setVue] = useState<'familles' | 'catalogue'>('familles');

  // Familles
  const [familles, setFamilles] = useState<ScolaireFamille[]>([]);
  const [q, setQ] = useState('');
  const [familleActive, setFamilleActive] = useState<ScolaireFamille | null>(null);
  const [loading, setLoading] = useState(true);

  // Catalogue
  const [types, setTypes] = useState<PieceType[]>([]);
  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [specifique, setSpecifique] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setFamilles(await api.scolaire.listFamilles()); }
      catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement des familles'); }
      finally { setLoading(false); }
    })();
  }, []);

  async function chargerCatalogue() {
    try { setTypes((await api.famillePieces.typesAdmin('petite_enfance')).pieceTypes); }
    catch { setError('Impossible de charger le catalogue de pièces.'); }
  }
  useEffect(() => { if (vue === 'catalogue') void chargerCatalogue(); }, [vue]);

  async function toggleType(t: PieceType) {
    setError(null);
    try {
      await api.famillePieces.setTypeActif(t.id, !t.actif);
      setTypes((prev) => prev.map((x) => (x.id === t.id ? { ...x, actif: !x.actif } : x)));
    } catch { setError('Modification impossible.'); }
  }

  async function ajouterType() {
    if (!code.trim() || !libelle.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.famillePieces.createType({
        code: code.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        libelle: libelle.trim(),
        portee: specifique ? 'specifique' : 'commun',
        module: specifique ? 'petite_enfance' : null,
      });
      setCode(''); setLibelle(''); setSpecifique(false);
      await chargerCatalogue();
    } catch { setError('Création impossible (code déjà utilisé ?).'); }
    finally { setSaving(false); }
  }

  const filtre = q.trim().toLowerCase();
  const listeFamilles = filtre ? familles.filter((f) => f.nom.toLowerCase().includes(filtre)) : familles;

  return (
    <GrcLayout active="dossiers">
      <main id="main-content" className="mx-auto max-w-[1100px] px-4 py-5">
        <div className="mb-4 flex items-center gap-3">
          <LogoMark icon={Baby} accent="grc" size="lg" />
          <div>
            <SectionEyebrow as="div">GRC &amp; communication</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">Dossiers petite enfance</h1>
            <p className="text-[11px] text-ink-subtle">
              Une pièce déposée par une famille sert tous ses dossiers — on ne redemande que ce qui manque.
            </p>
          </div>
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

        {/* Sous-onglets */}
        <div className="mb-4 flex gap-2">
          <Button type="button" variant={vue === 'familles' ? 'grc' : 'secondary'} size="sm" onClick={() => { setVue('familles'); setFamilleActive(null); }}>
            <Users className="h-4 w-4" aria-hidden="true" /> Familles
          </Button>
          {isAdmin && (
            <Button type="button" variant={vue === 'catalogue' ? 'grc' : 'secondary'} size="sm" onClick={() => setVue('catalogue')}>
              <Settings className="h-4 w-4" aria-hidden="true" /> Pièces demandées
            </Button>
          )}
        </div>

        {vue === 'familles' ? (
          familleActive ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-medium text-ink">Famille {familleActive.nom}</div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setFamilleActive(null)}>← Toutes les familles</Button>
              </div>
              <FamillePiecesPanel familleId={familleActive.id} module="petite_enfance" accent="grc" canValidate setError={setError} />
            </div>
          ) : (
            <>
              <div className="relative mb-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" aria-hidden="true" />
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une famille…"
                  className="h-10 w-full rounded-r-lg border border-border bg-surface/60 pl-10 pr-3 text-sm text-ink focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc" />
              </div>
              {loading ? (
                <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div></Card>
              ) : listeFamilles.length === 0 ? (
                <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Aucune famille.</div></Card>
              ) : (
                <div className="space-y-2">
                  {listeFamilles.map((f) => (
                    <Card key={f.id} variant="glass" className="flex items-center justify-between p-3">
                      <span className="font-medium text-ink">{f.nom}</span>
                      <Button type="button" variant="grc" size="sm" onClick={() => setFamilleActive(f)}>Voir les pièces</Button>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )
        ) : (
          <div className="space-y-3">
            <p className="text-[11px] text-ink-subtle">
              Activez les natures de pièces que vous réclamez aux familles en petite enfance.
              Une pièce désactivée n'est plus demandée. Les pièces communes servent aussi au scolaire ;
              les « spécifiques PE » (vaccins, PAI…) restent propres à la petite enfance.
            </p>
            <div className="space-y-1.5">
              {types.map((t) => (
                <Card key={t.id} variant="glass" className={`flex items-center justify-between gap-3 p-3 ${t.actif ? '' : 'opacity-60'}`}>
                  <span className="min-w-0 flex-1 text-sm text-ink">{t.libelle}</span>
                  <Badge variant={t.portee === 'specifique' ? 'warning' : 'neutral'} size="sm">
                    {t.portee === 'specifique' ? 'spécifique PE' : 'commun'}
                  </Badge>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={t.actif}
                    aria-label={`${t.actif ? 'Ne plus demander' : 'Demander'} ${t.libelle}`}
                    onClick={() => void toggleType(t)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition-colors ${t.actif ? 'border-grc/60 bg-grc/70' : 'border-border bg-surface-hi ring-1 ring-inset ring-border'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-soft transition-transform ${t.actif ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </Card>
              ))}
            </div>
            <Card variant="glass" padded>
              <div className="mb-2 text-sm font-medium text-ink">Ajouter une nature de pièce</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Libellé (ex. Attestation employeur)"
                  className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none sm:col-span-2" />
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="code_technique"
                  className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-[11px] text-ink-muted">
                  <input type="checkbox" checked={specifique} onChange={(e) => setSpecifique(e.target.checked)} className="h-3.5 w-3.5" />
                  Pièce propre à la petite enfance (ne sert pas au scolaire)
                </label>
                <Button type="button" variant="grc" size="sm" disabled={saving || !code.trim() || !libelle.trim()} onClick={() => void ajouterType()}>
                  {saving ? 'Ajout…' : 'Ajouter'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
    </GrcLayout>
  );
}
